import moment from 'moment';
import { jsPDF } from 'jspdf';
import { takeUntil } from 'rxjs/operators';

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { MatDialog } from '@angular/material/dialog';

import { CoreComponent } from '@sociuu/core';

import { AppService } from '@app/app.service';
import { DashboardService, IUsergroupStatPayload } from './dashboard.service';

import {
  DateIntervals,
  EngagementDataDto,
  AdoptionRateDataDto,
  StatisticsNumberDto,
  PostFrequencyDataDto,
  TopPerformingGroupsDataDto,
} from '@sociuu/interfaces';
import { IPostFrequency } from '@lib/dashboard.interface';

@Component({
  selector: 'sociuu-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent extends CoreComponent implements OnInit {
  public clientCurrencySymbol: string;
  statistics: StatisticsNumberDto;

  topPerformingGroups: TopPerformingGroupsDataDto[] = [];

  adoptionRateStats: AdoptionRateDataDto[] = [];

  frequencyRatioStats: PostFrequencyDataDto[] = [];

  tedStats: EngagementDataDto[] = [];

  dateIntervals = Object.entries(DateIntervals).map(([key, value]) => ({
    key,
    value,
  }));

  form: FormGroup;

  statsNum: number;

  adoptionRateChartData: any = [];
  frequencyRatioChartData: any = [];
  topEngagementChartData: any = [];
  loadingTextAdoptionRate: string = 'Working';
  loadingTextTopEngagement: string = 'Working';
  loadingTextFrequencyRatio: string = 'Working';
  loadingTextTopPerformingGroups: string = 'Working';

  tickAmount: number;

  frequencyRatioChart: {
    chartTitle: string;
    title: any;
    labels: string[];
    chartData: IPostFrequency[];
    series: any[];
    xAxisType: 'category' | 'datetime' | 'numeric';
  } = {
    title: (data): string => {
      return `
        <div class="arrow_box">
          <span> ${data.series[data.seriesIndex][data.dataPointIndex]} post(s) created in
          ${moment(this.frequencyRatioChartData[data.dataPointIndex]?.fullDate).format('MMM-DD-YYYY')}</span>
        </div>`;
    },
    chartTitle: 'Frequency ratio',
    labels: [],
    chartData: [],
    series: [
      {
        name: 'Frequency ratio',
        type: 'scatter',
        data: [],
      },
    ],
    xAxisType: 'datetime',
  };

  topEngagementChart: {
    chartTitle: string;
    title: any;
    labels: string[];
    chartData: EngagementDataDto[];
    series: any[];
  } = {
    title: (data): string => {
      return `
        <div class="arrow_box">
          <span> ${data.series[data.seriesIndex][data.dataPointIndex]} shares in
          ${this.topEngagementChartData[data.dataPointIndex].date_month}/${
        this.topEngagementChartData[data.dataPointIndex].date_year
      }</span>
        </div>`;
    },
    chartTitle: 'Total engagement development',
    labels: [],
    chartData: [],
    series: [
      {
        data: [],
        name: 'Total engagement development',
        type: 'area',
        color: 'rgba(54, 162, 235, 0.2)',
      },
    ],
  };

  adoptionRateChart: {
    chartTitle: string;
    title: any;
    labels: string[];
    chartData: AdoptionRateDataDto[];
    series: any[];
  } = {
    series: [
      {
        name: 'Total Unique',
        data: [],
        color: 'rgba(217, 217, 217, 1)',
        type: 'area',
      },
      {
        name: 'Campaign',
        data: [],
        color: 'rgba(68, 114, 196, 1)',
        type: 'line',
      },
      {
        name: 'Pull',
        data: [],
        color: 'rgba(190, 81, 1, 1)',
        type: 'line',
      },
    ],
    chartTitle: 'Adoption Rate',
    title: (data) => {
      let averageFigure;
      const dataSet = this.adoptionRateChartData[data.dataPointIndex];
      const seriesName: string = data.w.globals.seriesNames[data.seriesIndex];

      if (seriesName.toLowerCase() == 'total unique') {
        averageFigure = dataSet.shared;
      } else if (seriesName.toLowerCase() == 'campaign') {
        averageFigure = dataSet.shared_push;
      } else if (seriesName.toLowerCase() == 'pull') {
        averageFigure = dataSet.shared_pull;
      }
      return `
        <div class="arrow_box">
          <h3>${seriesName}</h3>
          <span> ${Math.round(data.series[data.seriesIndex][data.dataPointIndex])}% ${seriesName}</span> <br />
          <span> ${averageFigure} out of ${dataSet.user_base} adapted</span> <br />
          <span> in ${dataSet.full_date}</span>
        </div>`;
    },
    labels: [],
    chartData: [],
  };

  constructor(
    public dialog: MatDialog,
    private dashboardService: DashboardService,
    private fb: FormBuilder,
    private appService: AppService,
  ) {
    super();

    this.form = this.fb.group({
      stats_interval: ['365d'],
      performance_interval: ['365d'],
      date_from: [''],
      date_to: [''],
    });
  }

  ngOnInit(): void {
    this.getCurrencySymbol();
    this.getStats();
    this.getEngagementStats();
    this.getEngagementUsergroupsStats();

    // For New separated Apis
    this.getPostFrequencyDashboardStats();
    this.getAdoptionRateDashboardStats();
  }

  getStats(): void {
    const options = {
      interval: this.form.get('stats_interval').value,
      date_from: this.form.get('date_from').value,
      date_to: this.form.get('date_to').value,
    };
    this.dashboardService
      .getStats(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response) => {
        this.statistics = response.data.numbers;
        this.statsNum = response.total;
      });
  }

  getAdoptionRateDashboardStats() {
    const options = {
      interval: this.form.get('performance_interval').value,
      groupBy: 'month',
      date_from: this.form.get('date_from').value,
      date_to: this.form.get('date_to').value,
    };
    this.dashboardService
      .getAdoptionRateDashboardStats(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        if (!res.data.length) this.loadingTextAdoptionRate = 'No data available';

        this.adoptionRateChart.chartData = res.data;
        this.adoptionRateChartData = res.data;
        this.adoptionRateChart.labels = res.data.length ? res.data.map((val) => `${val.date_month}/${val.date_year}`) : [];
        this.adoptionRateChart.series[0].data = res.data.some((x) => +x.adaption_rate_pull >= 0)
          ? res.data.map((x) => Math.round(+x.adaption_rate))
          : [];
        this.adoptionRateChart.series[1].data = res.data.length ? res.data.map((x) => Math.round(+x.adaption_rate_push)) : [];
        this.adoptionRateChart.series[2].data = res.data.some((x) => +x.adaption_rate_pull >= 0)
          ? res.data.map((x) => Math.round(+x.adaption_rate_pull))
          : [];
      });
  }

  getPostFrequencyDashboardStats() {
    const options = {
      interval: this.form.get('performance_interval').value,
      groupBy: 'month',
      date_from: this.form.get('date_from').value,
      date_to: this.form.get('date_to').value,
    };
    this.dashboardService
      .getPostFrequencyDashboardStats(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res: IPostFrequency[]) => {
        if (res?.length) {
          this.frequencyRatioChart.series[0].data = res.map((val) => {
            const date = new Date(val?.fullDate).getTime();
            return [date, val.postsSent];
          });

          this.frequencyRatioChart.series[0].data.push([new Date().getTime(), null]);

          const postsSent = this.frequencyRatioChart?.series[0]?.data?.map((n) => n[1]);
          this.tickAmount = Math.max(...postsSent) > 10 ? Math.ceil(Math.max(...postsSent) / 10) : 5;

          this.frequencyRatioChart.chartData = res;
          this.frequencyRatioChartData = res;
        } else {
          this.loadingTextFrequencyRatio = 'No data available';
          this.frequencyRatioChart.chartData = [];
          this.frequencyRatioChartData = [];
          this.frequencyRatioChart.series[0].data = []
        }
      });
  }

  getEngagementStats(): void {
    const options = {
      groupBy: 'month',
      date_to: this.form.get('date_to').value,
      date_from: this.form.get('date_from').value,
      interval: this.form.get('performance_interval').value,
    };
    this.dashboardService
      .getNewEngagementStats(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response) => {
        if (response.data) {
          this.topEngagementChart.chartData = response.data;
          this.topEngagementChartData = response.data;
          if (this.topEngagementChart.chartData.length <= 0) {
            this.loadingTextTopEngagement = 'No data available';
          }

          this.topEngagementChart.series[0].data = response.data.length
            ? response.data.map((val) => +val.shares_push_and_pull)
            : [];

          this.topEngagementChart.labels = response.data.length
            ? response.data.map((val) => `${val.date_month}/${val.date_year}`)
            : [];
        }
      });
  }

  getEngagementUsergroupsStats(): void {
    const options: IUsergroupStatPayload = {
      groupBy: 'month',
      dateTo: this.form.get('date_to').value,
      dateFrom: this.form.get('date_from').value,
      interval: this.form.get('performance_interval').value,
    };
    this.dashboardService
      .getEngagementUsergroupsStats(options)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response) => {
        if (!response.data.length) this.loadingTextTopPerformingGroups = 'There is no data for the selected date interval';
        this.topPerformingGroups = response.data;
      });
  }

  public setInterval(intervalType: string, interval: string): void {
    if (intervalType === 'stats') {
      this.form.get('stats_interval').setValue(interval);
      this.getStats();
    } else if (intervalType === 'performance') {
      this.form.get('performance_interval').setValue(interval);
      this.getEngagementStats();
      this.getEngagementUsergroupsStats();

      // For New separated Apis
      this.getPostFrequencyDashboardStats();
      this.getAdoptionRateDashboardStats();
    }
  }

  public setCustomInterval(intervalType: string, customDateRange: { from: any, to: any }): void {
    if (customDateRange) {
      const date_to = customDateRange.to;
      const date_from = customDateRange.from;
      this.form.get('date_to').setValue(date_to);
      this.form.get('date_from').setValue(date_from);

      if (intervalType === 'stats') {
        this.form.get('stats_interval').setValue('custom');
        this.getStats();
      } else if (intervalType === 'performance') {
        this.form.get('performance_interval').setValue('custom');
        this.getEngagementStats();
        this.getEngagementUsergroupsStats();

        // For New separated Apis
        this.getPostFrequencyDashboardStats();
        this.getAdoptionRateDashboardStats();
      }
    } else {
      if (intervalType === 'stats') {
        this.form.get('stats_interval').setValue('365d');
      } else if (intervalType === 'performance') {
        this.form.get('performance_interval').setValue('365d');
      }
    }
  }

  exportChart(event) {
    const { extension, chartId, fileName } = event;
    const canvas: HTMLCanvasElement = document.getElementById(chartId) as HTMLCanvasElement;
    const img = canvas.toDataURL('image/png');

    if (extension === 'pdf') {
      const pdf = new jsPDF('l', 'px', 'a4');
      const i = new Image();
      i.src = img;
      pdf.addImage(i, 'png', 10, 10, i.width, i.height);
      pdf.save(`${fileName}.pdf`);
    } else {
      const a = document.getElementById('ar') as HTMLAnchorElement;
      a.href = canvas.toDataURL('image/png');
      a.download = `${fileName}.png`;
      a.click();
    }
  }

  private getCurrencySymbol(): void {
    this.clientCurrencySymbol = this.appService.currencySymbol;
  }
}
